'use client';

import { lookup, type Vehicle } from '@cruz-com/lookup';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import { useQuery } from '@tanstack/react-query';
import _ from 'lodash';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { HiArrowUturnLeft } from 'react-icons/hi2';
import slugify from 'slugify';
import { twMerge } from 'tailwind-merge';
import { vehicleApi } from '@/api';
import { AutoComplete, Button, Checkbox, CruzIcon, ListBox, PageLoading, Radio, Text, Toggle } from '@/components';
import { radiusOption } from '@/settings';
import { useGlobalStore } from '@/store/provider';
import { Section } from './vehicleFilterSection';
import { MakeModeTrim } from './vehicleMakeModelTrim';
type Props = {
  category?: 'electric' | 'phev' | 'exotic' | 'luxury' | 'sport' | 'heavyHauler' | 'offRoad' | 'convertible' | 'fuelEfficient' | 'family' | 'private' | 'antique' | 'classic' | 'cheap' | 'wheelchairAccessible' | 'certified' | 'highlyReliable' | 'veryComfortable' | 'funToDrive' | 'highTech' | 'lowMaintenance';
  bodyTypeId?: number | string;
  ignoreFilters?: string[];
  defaultQuery: Vehicle.Query;
  onFilterChangedAction: (query: Vehicle.Query) => void;
  btnType?: string;
};
const groupStyle = 'w-full divide-y border rounded-md bg-zinc-100/80 dark:bg-zinc-800/80';
type Option = {
  id: string | number;
  name: string;
  disabled?: boolean;
};
const featureGroups = _.chain(lookup.vehicle.featureTag).filter({
  searchable: true
}).sortBy('category').groupBy('category').mapValues(g => _.chain(g).map(item => item.name).sortBy().value()).value();
export function VehicleFilter1({
  category,
  bodyTypeId,
  ignoreFilters,
  defaultQuery,
  onFilterChangedAction,
  btnType
}: Props) {
  const pathname = usePathname();
  const {
    scrollTopOver
  } = useGlobalStore(state => state);
  const [showFilter, setShowFilter] = useState(false);
  const [query, setQuery] = useState<Vehicle.Query>(defaultQuery);
  const [loc, setLoc] = useState<Pick<Vehicle.Query, 'zip_code' | 'radius'>>(_.pick(defaultQuery, ['zip_code', 'radius']));
  const [mounted, setMounted] = useState<boolean>(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  useEffect(() => {
    setTimeout(() => {
      setQuery(_.omit(defaultQuery, 'make', 'model', 'trim'));
      // setKeyword(defaultQuery?.keyword || '')
      setLoc(_.pick(defaultQuery, ['zip_code', 'radius']));
    }, 50);
  }, [defaultQuery]);
  function getQuery() {
    if (bodyTypeId) {
      return _.pickBy({
        ...query,
        body_type: bodyTypeId
      }, _.identity);
    }
    return _.pickBy(query, _.identity);
  }

  // get options
  const {
    data,
    isFetching,
    refetch
  } = useQuery({
    queryKey: [`vehicleFilterOptions_${category || 'none'}`],
    queryFn: () => vehicleApi.getFilterOption(getQuery(), category),
    staleTime: 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false
  });

  // checkbox changed - multiple selection
  function checkboxChanged({
    key,
    id,
    val
  }: {
    key: string;
    id: number | string;
    val: boolean;
  }) {
    const d = _.get(query, key);
    const x: string[] = d ? _.split(d as string, ',') : [];
    const y = val ? _.concat(x, `${id}`) : _.without(x, `${id}`);
    if (_.isEmpty(y)) {
      return setQuery(_.omit(query, key));
    }
    setQuery({
      ...query,
      [key]: _.join(y, ',')
    });
  }

  // (dropdown or radio) changed - single selection
  function radioChanged({
    name,
    id: id_0
  }: {
    name: string;
    id: number | string;
  }) {
    if (!_.toNumber(id_0)) {
      return setQuery(_.omit(query, name));
    }
    return setQuery({
      ...query,
      [name]: id_0
    });
  }

  // toggle (switch) changed
  function toggleChanged(name_0: string, isEnabled: boolean) {
    if (isEnabled) {
      return setQuery({
        ...query,
        [name_0]: 1
      });
    }
    setQuery(_.omit(query, name_0));
  }

  // mmt (make, model, trim) changed
  function mmtChanged(name_1: string, val_0: string | null) {
    if (!_.get(query, name_1) && !val_0) {
      return;
    }
    if (_.isEmpty(val_0)) {
      return setQuery(_.omit(query, name_1));
    }
    setQuery({
      ...query,
      [name_1]: val_0
    });
  }

  // ---- location ----
  function locChanged(name_2: string, value: string | number) {
    let updateValue = value;
    if (name_2 === 'zip_code') {
      updateValue = (value as string).replace(/\D/g, ''); // Remove all non-digit characters
    }
    const d_0 = {
      ...loc,
      [name_2]: updateValue
    };
    const zLen = _.size(d_0.zip_code);
    setLoc(d_0);
    if (zLen === 5 || zLen === 0 && _.size(loc.zip_code) > 0) {
      if ((d_0.radius || 0) > 0 || (d_0.radius || 0) < 0 && (loc.radius || 0) > 0) {
        setQuery({
          ...query,
          ...d_0
        });
      }
    }
  }

  // apply filter
  function applyFilter() {
    setShowFilter(false);
    delete query.trace;
    _.keys(query).forEach(key_0 => {
      const k = key_0 as keyof Vehicle.Query;
      const d_1 = _.toNumber(query[k]);
      if (d_1 < 0) {
        delete query[k];
      }
    });
    setTimeout(() => {
      onFilterChangedAction(query);
    }, 100);
  }

  // default query changed (reset query)
  useEffect(() => {
    if (_.isEmpty(defaultQuery) && !_.isEmpty(query)) {
      setQuery({});
    }
  }, [defaultQuery]);

  // query changed - get new options
  useEffect(() => {
    refetch();
  }, [query]);

  // data changed (fetched) - set query
  useEffect(() => {
    // remove unavailable filter options
    const qKeys = _.pull(_.keys(query), 'page_number', 'page_size', 'sort_id', 'sort_value');
    _.forEach(qKeys, key_1 => {
      const lkp = _.get(data?.data, key_1);
      if (!lkp) {
        return;
      }
      const q = _.split(_.get(query, key_1), ',');
      const nq: string[] = [];
      _.forEach(q, id_1 => {
        const x_0 = _.find(lkp, d_2 => {
          return d_2?.id?.toString() === id_1?.toString();
        });
        if (x_0) {
          nq.push(id_1);
        }
      });
      if (nq.length > 0) {
        _.assign(query, {
          [key_1]: _.join(nq, ',')
        });
      }
    });
  }, [data?.data]);

  // options
  const {
    condition,
    body_type,
    sale_status,
    price,
    mileage,
    year_min,
    year_max,
    make,
    highly_rated,
    // powertrain
    mpg,
    drive_type,
    fuel_type,
    trans_type,
    trans_gears,
    towing_capacity,
    engine_cylinders,
    engine_aspiration,
    engine_displacement,
    acceleration,
    horsepower,
    torque,
    power_to_weight,
    electric_range,
    // specifications
    exterior_color,
    interior_color,
    upholstery,
    doors,
    max_seating,
    bed_length,
    cab_style,
    size,
    // other
    feature,
    country
  } = data?.data || {};
  const renderBodyType = () => ignoreFilters?.includes('body_type') ? null : <fieldset>
        <legend className='filter-label mb-2'>Body Style</legend>
        <ul className='grid w-full grid-cols-2 gap-x-3 sm:grid-cols-3'>
          {_.map(body_type, ({
        id: id_2,
        name: name_3,
        disabled
      }) => <li key={id_2}>
              <Checkbox id={`body_type_${id_2}`} label={name_3} checked={_.split(_.toString(query?.body_type), ',').includes(id_2?.toString())} onChange={val_1 => checkboxChanged({
          key: 'body_type',
          id: id_2,
          val: val_1
        })} disabled={disabled} />
            </li>)}
        </ul>
      </fieldset>;
  const yearOption = (() => {
    if (!year_min || !year_max) {
      return [{
        id: -1,
        name: 'Any'
      }];
    }
    const d_3 = _.map(_.range(year_max, (year_min || 1901) - 1), y_0 => {
      return {
        id: y_0,
        name: y_0.toString()
      };
    });
    return [{
      id: -1,
      name: 'Any'
    }, ...d_3];
  })();
  const renderCondition = () => ignoreFilters?.includes('condition') ? null : <fieldset>
        <legend className='filter-label mb-2'>Condition</legend>
        <ul className='grid grid-cols-2 flex-col gap-x-8 sm:flex sm:flex-row'>
          {_.map(condition, ({
        id: id_3,
        name: name_4,
        disabled: disabled_0
      }) => <li key={id_3}>
              <Checkbox id={`condition_${id_3}`} label={name_4} checked={_.split(query?.condition, ',').includes(id_3?.toString())} onChange={val_2 => checkboxChanged({
          key: 'condition',
          id: id_3,
          val: val_2
        })} disabled={disabled_0} />
            </li>)}
        </ul>
      </fieldset>;
  const renderYear = () => <fieldset>
      <legend className='filter-label mb-2'>
        Year <span className='text-xs font-light'>(Min to Max)</span>
      </legend>
      <div className='flex w-fit items-end gap-x-3'>
        <div className='w-28'>
          <ListBox compact={true} option={yearOption} value={query?.year_min || -1} className='w-32' onChange={val_3 => {
          radioChanged({
            name: 'year_min',
            id: val_3 as number
          });
        }} />
        </div>
        <span className='text-sm/8'>to</span>
        <div className='w-28'>
          <ListBox compact={true} option={yearOption} value={query?.year_max || -1} className='w-32' onChange={val_4 => {
          radioChanged({
            name: 'year_max',
            id: val_4 as number
          });
        }} />
        </div>
      </div>
    </fieldset>;
  const renderPrice = () => {
    if (ignoreFilters?.includes('price')) return null;
    const handlePriceMinChange = (val_5: unknown) => radioChanged({
      name: 'price_min',
      id: val_5 as number
    });
    const handlePriceMaxChange = (val_6: unknown) => radioChanged({
      name: 'price_max',
      id: val_6 as number
    });
    return <fieldset>
        <legend className='filter-label mb-1'>
          Price <span className='text-xs font-light'>(Min to Max)</span>
        </legend>
        <div className='flex w-fit items-end gap-x-3'>
          <div className='w-36'>
            <ListBox compact option={price || []} value={query?.price_min ?? -1} className='w-32' onChange={handlePriceMinChange} />
          </div>
          <span className='text-sm/8'>to</span>
          <div className='w-36'>
            <ListBox compact option={price || []} value={query?.price_max ?? -1} className='w-32' onChange={handlePriceMaxChange} />
          </div>
        </div>
      </fieldset>;
  };
  const renderMileage = () => <fieldset className='w-64'>
      <legend className='filter-label mb-2'>
        Mileage <span className='text-xs font-light'>(Less than)</span>
      </legend>
      <ListBox compact={true} option={mileage || []} value={query?.mileage || -1} disabled={_.size(mileage) < 2} onChange={val_7 => {
      radioChanged({
        name: 'mileage',
        id: val_7 as number
      });
    }} />
    </fieldset>;
  const renderHighlyRated = () => {
    return <fieldset>
        <legend className='filter-label'>Highly Rated</legend>
        <p className='mt-1 text-xs font-light'>Note: Based on opinions and are intended as general guidance only</p>
        <div className='mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2'>
          {!ignoreFilters?.includes('reliability') && <Toggle label='Highly Reliable' name='reliability' checked={_.toNumber(query?.reliability) === 1} onChange={isEnabled_0 => toggleChanged('reliability', isEnabled_0)} disabled={!_.isEmpty(highly_rated) && !highly_rated.reliability} />}
          {!ignoreFilters?.includes('maintenance') && <Toggle label='Low Maintenance' name='maintenance' checked={_.toNumber(query?.maintenance) === 1} onChange={isEnabled_1 => toggleChanged('maintenance', isEnabled_1)} />}
          {!ignoreFilters?.includes('comfort') && <Toggle label='Very Comfortable' name='comfort' checked={_.toNumber(query?.comfort) === 1} onChange={isEnabled_2 => toggleChanged('comfort', isEnabled_2)} />}
          {!ignoreFilters?.includes('technology') && <Toggle label='High-Tech' name='technology' checked={_.toNumber(query?.technology) === 1} onChange={isEnabled_3 => toggleChanged('technology', isEnabled_3)} disabled={!_.isEmpty(highly_rated) && !highly_rated.technology} />}
          {!ignoreFilters?.includes('dynamics') && <Toggle label='Fun to Drive' name='dynamics' checked={_.toNumber(query?.dynamics) === 1} onChange={isEnabled_4 => toggleChanged('dynamics', isEnabled_4)} />}
        </div>
      </fieldset>;
  };
  const renderDriveType = () => ignoreFilters?.includes('drive_type') ? null : <Section name='driveType' title='Drive Type' hasValue={!_.isEmpty(query?.drive_type)}>
        {!isFetching && _.isEmpty(drive_type) && <NoOption />}
        {!_.isEmpty(drive_type) && <ul className='grid grid-cols-2 gap-x-2 sm:grid-cols-3'>
            {_.map(drive_type, ({
        id: id_4,
        name: name_5,
        disabled: disabled_1
      }) => <li key={id_4}>
                <Checkbox id={`drive_type_${id_4}`} label={name_5} checked={_.split(query?.drive_type, ',').includes(id_4.toString())} onChange={val_8 => checkboxChanged({
          key: 'drive_type',
          id: id_4,
          val: val_8
        })} disabled={disabled_1} />
              </li>)}
          </ul>}
      </Section>;
  const renderFuelEconomy = () => ignoreFilters?.includes('mpg') ? null : <Section name='mpg' title='Fuel Economy' description='mpg' hasValue={!_.isEmpty(query?.mpg)}>
        {!isFetching && _.isEmpty(mpg) && <NoOption />}
        {!_.isEmpty(mpg) && <ul className='grid grid-cols-3 gap-x-2 sm:grid-cols-6'>
            {_.map(mpg, ({
        id: id_5,
        name: name_6,
        disabled: disabled_2
      }) => <li key={id_5}>
                <Radio name='mpg' id={_.toString(id_5)} label={name_6} checked={query?.mpg?.toString() === id_5.toString()} onChange={() => radioChanged({
          name: 'mpg',
          id: id_5
        })} disabled={disabled_2} />
              </li>)}
          </ul>}
      </Section>;
  const renderFuelType = () => ignoreFilters?.includes('fuel_type') ? null : <Section name='fuelType' title='Fuel Type' hasValue={!_.isEmpty(query?.fuel_type)}>
        {!isFetching && _.isEmpty(fuel_type) && <NoOption />}
        {!_.isEmpty(fuel_type) && <ul className='grid grid-cols-1 gap-x-2 sm:grid-cols-3'>
            {_.map(fuel_type, ({
        id: id_6,
        name: name_7,
        disabled: disabled_3
      }) => <li key={id_6}>
                <Checkbox id={`fuel_type_${id_6}`} label={name_7} checked={_.split(query?.fuel_type, ',').includes(id_6.toString())} onChange={val_9 => checkboxChanged({
          key: 'fuel_type',
          id: id_6,
          val: val_9
        })} disabled={disabled_3} />
              </li>)}
          </ul>}
      </Section>;
  const renderTransType = () => ignoreFilters?.includes('trans_type') ? null : <Section name='transType' title='Transmission Type' hasValue={!_.isEmpty(query?.trans_type)}>
        {!isFetching && _.isEmpty(trans_type) && <NoOption />}
        {!_.isEmpty(trans_type) && <ul className='grid grid-cols-1 gap-x-2 sm:grid-cols-3'>
            {_.map(trans_type, ({
        id: id_7,
        name: name_8,
        disabled: disabled_4
      }) => <li key={id_7}>
                <Checkbox id={`trans_type_${id_7}`} label={name_8} checked={_.split(query?.trans_type, ',').includes(id_7.toString())} onChange={val_10 => checkboxChanged({
          key: 'trans_type',
          id: id_7,
          val: val_10
        })} disabled={disabled_4} />
              </li>)}
          </ul>}
      </Section>;
  const renderTransGears = () => ignoreFilters?.includes('trans_gears') ? null : <Section name='transGears' title='Transmission Gears' hasValue={!_.isEmpty(query?.trans_gears)}>
        {!isFetching && _.isEmpty(trans_gears) && <NoOption />}
        {!_.isEmpty(trans_gears) && <ul className='grid grid-cols-4 gap-x-2 sm:grid-cols-6'>
            {_.map(trans_gears as Option[], ({
        id: id_8,
        name: name_9,
        disabled: disabled_5
      }) => <li key={id_8}>
                <Checkbox id={`trans_gears_${id_8}`} label={name_9} checked={_.split(query?.trans_gears?.toString(), ',').includes(id_8.toString())} onChange={val_11 => checkboxChanged({
          key: 'trans_gears',
          id: _.toNumber(id_8),
          val: val_11
        })} disabled={disabled_5} />
              </li>)}
          </ul>}
      </Section>;
  const renderEngineAspiration = () => ignoreFilters?.includes('engine_aspiration') ? null : <Section name='engAspiration' title='Engine Aspiration' hasValue={!_.isEmpty(query?.engine_aspiration)}>
        {!isFetching && _.isEmpty(engine_aspiration) && <NoOption />}
        {!_.isEmpty(engine_aspiration) && <ul className='grid grid-cols-1 gap-x-2 sm:grid-cols-3'>
            {_.map(engine_aspiration, ({
        id: id_9,
        name: name_10,
        disabled: disabled_6
      }) => <li key={id_9}>
                <Checkbox id={`eng_aspiration_${id_9}`} label={name_10} checked={_.split(query?.engine_aspiration?.toString(), ',').includes(id_9.toString())} onChange={val_12 => checkboxChanged({
          key: 'engine_aspiration',
          id: id_9,
          val: val_12
        })} disabled={disabled_6} />
              </li>)}
          </ul>}
      </Section>;
  const renderEngineCylinder = () => ignoreFilters?.includes('engine_cylinders') ? null : <Section name='engCylinder' title='Engine Cylinders' hasValue={!_.isEmpty(query?.engine_cylinders)}>
        {!isFetching && _.isEmpty(engine_cylinders) && <NoOption />}
        {!_.isEmpty(engine_cylinders) && <ul className='grid grid-cols-4 gap-x-2 sm:grid-cols-5'>
            {_.map(engine_cylinders, ({
        id: id_10,
        name: name_11,
        disabled: disabled_7
      }) => <li key={id_10}>
                <Checkbox id={`eng_cylinder_${id_10}`} label={name_11} checked={_.split(query?.engine_cylinders?.toString(), ',').includes(id_10.toString())} onChange={val_13 => checkboxChanged({
          key: 'engine_cylinders',
          id: id_10,
          val: val_13
        })} disabled={disabled_7} />
              </li>)}
          </ul>}
      </Section>;
  const renderEngineDisplacement = () => ignoreFilters?.includes('engine_displacement') ? null : <Section name='engDisplacement' title='Engine Displacement' hasValue={!_.isEmpty(query?.engine_displacement)}>
        <div className='flex items-center'>
          <AutoComplete name='engine_displacement' inputMode='tel' className='w-20' option={engine_displacement || []} value={query?.engine_displacement?.toString()} onChange={val_14 => {
        radioChanged({
          name: 'engine_displacement',
          id: _.toString(val_14)
        });
      }} />
          <span className='pl-2 text-sm'>liters</span>
        </div>
      </Section>;
  const renderTowingCapacity = () => ignoreFilters?.includes('towing_capacity') ? null : <Section name='towing_capacity' title='Standard Towing Capacity' description='lbs' hasValue={!_.isEmpty(query?.towing_capacity)}>
        {!isFetching && _.isEmpty(towing_capacity) && <NoOption />}
        <ul className='grid grid-cols-3 gap-x-2 sm:grid-cols-4'>
          {_.map(towing_capacity, ({
        id: id_11,
        name: name_12,
        disabled: disabled_8
      }) => <li key={id_11}>
              <Radio name='towing_capacity' id={_.toString(id_11)} label={name_12} checked={query?.towing_capacity?.toString() === id_11.toString()} onChange={() => radioChanged({
          name: 'towing_capacity',
          id: id_11
        })} disabled={disabled_8} />
            </li>)}
        </ul>
      </Section>;
  const renderAcceleration = () => ignoreFilters?.includes('acceleration') ? null : <Section name='acceleration' title='Acceleration' description='0-60 mph, in seconds' hasValue={(query?.acceleration as number || 0) > 0}>
        {!isFetching && _.isEmpty(acceleration) && <NoOption />}
        {!_.isEmpty(acceleration) && <ul className='grid grid-cols-3 gap-x-2 sm:grid-cols-6'>
            {_.map(acceleration, ({
        id: id_12,
        name: name_13,
        disabled: disabled_9
      }) => <li key={id_12}>
                <Radio name='acceleration' id={_.toString(id_12)} label={name_13} checked={query?.acceleration?.toString() === id_12.toString()} onChange={() => radioChanged({
          name: 'acceleration',
          id: id_12
        })} disabled={disabled_9} />
              </li>)}
          </ul>}
      </Section>;
  const renderHorsepower = () => ignoreFilters?.includes('horsepower') ? null : <Section name='horsepower' title='Horsepower' description='hp' hasValue={(query?.horsepower as number || 0) > 0}>
        {!isFetching && _.isEmpty(horsepower) && <NoOption />}
        {!_.isEmpty(horsepower) && <ul className='grid grid-cols-3 gap-x-2 sm:grid-cols-6'>
            {_.map(horsepower, ({
        id: id_13,
        name: name_14,
        disabled: disabled_10
      }) => <li key={id_13}>
                <Radio name='horsepower' id={_.toString(id_13)} label={name_14} checked={query?.horsepower?.toString() === id_13.toString()} onChange={() => radioChanged({
          name: 'horsepower',
          id: id_13
        })} disabled={disabled_10} />
              </li>)}
          </ul>}
      </Section>;
  const renderTorque = () => ignoreFilters?.includes('torque') ? null : <Section name='torque' title='Torque' description='ft.lbf' className='rounded-b-md' hasValue={(query?.torque as number || 0) > 0}>
        {!isFetching && _.isEmpty(torque) && <NoOption />}
        {!_.isEmpty(torque) && <ul className='grid grid-cols-3 gap-x-2 sm:grid-cols-6'>
            {_.map(torque, ({
        id: id_14,
        name: name_15,
        disabled: disabled_11
      }) => <li key={id_14}>
                <Radio name='torque' id={_.toString(id_14)} label={name_15} checked={query?.torque?.toString() === id_14.toString()} onChange={() => radioChanged({
          name: 'torque',
          id: id_14
        })} disabled={disabled_11} />
              </li>)}
          </ul>}
      </Section>;
  const renderPowerToWeight = () => ignoreFilters?.includes('power_to_weight') ? null : <Section name='power_to_weight' title='Power to Weight Ratio' description='lbs/hp' hasValue={(query?.power_to_weight as number || 0) > 0}>
        {!isFetching && _.isEmpty(power_to_weight) && <NoOption />}
        {!_.isEmpty(power_to_weight) && <ul className='grid grid-cols-3 gap-x-2 sm:grid-cols-5'>
            {_.map(power_to_weight, ({
        id: id_15,
        name: name_16,
        disabled: disabled_12
      }) => <li key={id_15}>
                <Radio name='power_to_weight' id={_.toString(id_15)} label={name_16} checked={query?.power_to_weight?.toString() === id_15.toString()} onChange={() => radioChanged({
          name: 'power_to_weight',
          id: id_15
        })} disabled={disabled_12} />
              </li>)}
          </ul>}
        <div className='pt-2 text-sm/6 font-light'>
          It's the balance of muscle versus mass: the less weight each horsepower has to move, the faster you go
        </div>
      </Section>;
  const renderElectricRange = () => ignoreFilters?.includes('electric_range') ? null : <Section name='electric_range' title='Electric Range' description='miles' className='rounded-b-md' hasValue={(query?.electric_range as number || 0) > 0}>
        {!isFetching && _.isEmpty(electric_range) && <NoOption />}
        {!_.isEmpty(electric_range) && <ul className='grid grid-cols-3 gap-x-2 sm:grid-cols-6'>
            {_.map(electric_range, ({
        id: id_16,
        name: name_17,
        disabled: disabled_13
      }) => <li key={id_16}>
                <Radio name='electric_range' id={_.toString(id_16)} label={name_17} checked={query?.electric_range?.toString() === id_16.toString()} onChange={() => radioChanged({
          name: 'electric_range',
          id: id_16
        })} disabled={disabled_13} />
              </li>)}
          </ul>}
      </Section>;

  // ---- specifications ----
  const renderSeats = () => ignoreFilters?.includes('max_seating') ? null : <Section name='max_seating' title='Seats' hasValue={!_.isEmpty(query?.max_seating)}>
        {!isFetching && _.isEmpty(max_seating) && <NoOption />}
        {!_.isEmpty(max_seating) && <ul className='grid grid-cols-4 gap-x-2 sm:grid-cols-6'>
            {_.map(max_seating, ({
        id: id_17,
        name: name_18,
        disabled: disabled_14
      }) => <li key={id_17}>
                <Checkbox id={`max_seating_${id_17}`} label={name_18} checked={_.split(query?.max_seating?.toString(), ',').includes(id_17.toString())} onChange={val_15 => checkboxChanged({
          key: 'max_seating',
          id: id_17,
          val: val_15
        })} disabled={disabled_14} />
              </li>)}
          </ul>}
      </Section>;
  const renderDoors = () => {
    if (ignoreFilters?.includes('doors')) return null;
    return <Section name='doors' title='Doors' hasValue={!_.isEmpty(query?.doors)}>
        {!isFetching && _.isEmpty(doors) && <NoOption />}
        {!_.isEmpty(doors) && <ul className='grid grid-cols-2 gap-x-2 sm:grid-cols-4'>
            {_.map(doors, ({
          id: id_18,
          name: name_19,
          disabled: disabled_15
        }) => <li key={id_18}>
                <Checkbox id={`door_${id_18}`} label={name_19} checked={_.split(query?.doors?.toString(), ',').includes(id_18.toString())} onChange={val_16 => checkboxChanged({
            key: 'doors',
            id: id_18,
            val: val_16
          })} disabled={disabled_15} />
              </li>)}
          </ul>}
      </Section>;
  };
  const renderExteriorColor = () => <Section name='exteriorColor' title='Exterior Color' hasValue={!_.isEmpty(query?.exterior_color)}>
      {!isFetching && _.isEmpty(exterior_color) && <NoOption />}
      {!_.isEmpty(exterior_color) && <ul className='grid grid-cols-2 gap-2 sm:grid-cols-4'>
          {_.map(exterior_color, ({
        id: id_19,
        name: name_20,
        disabled: disabled_16
      }) => <li key={id_19}>
              <Checkbox id={`ext_color_${id_19}`} label={<>
                    {name_20}
                    <div className={`v-${slugify(name_20 as string, {
            lower: true
          })}`} />
                  </>} checked={_.split(query?.exterior_color, ',').includes(id_19.toString())} onChange={val_17 => checkboxChanged({
          key: 'exterior_color',
          id: id_19,
          val: val_17
        })} disabled={disabled_16} />
            </li>)}
        </ul>}
    </Section>;
  const renderInteriorColor = () => <Section name='interiorColor' title='Interior Color' hasValue={!_.isEmpty(query?.interior_color)}>
      {!isFetching && _.isEmpty(interior_color) && <NoOption />}
      {!_.isEmpty(interior_color) && <ul className='grid grid-cols-2 gap-2 sm:grid-cols-4'>
          {_.map(interior_color, ({
        id: id_20,
        name: name_21,
        disabled: disabled_17
      }) => <li key={id_20}>
              <Checkbox id={`int_color_${id_20}`} label={<>
                    {name_21}
                    <div className={`v-${slugify(name_21 as string, {
            lower: true
          })}`} />
                  </>} checked={_.split(query?.interior_color, ',').includes(id_20.toString())} onChange={val_18 => checkboxChanged({
          key: 'interior_color',
          id: id_20,
          val: val_18
        })} disabled={disabled_17} />
            </li>)}
        </ul>}
    </Section>;
  const renderBedLength = () => ignoreFilters?.includes('bed_length') ? null : <Section name='bed_length' title='Pickup Bed Length' description='inches' hasValue={(query?.bed_length as number || 0) > 0}>
        {!isFetching && _.isEmpty(bed_length) && <NoOption />}
        {!_.isEmpty(bed_length) && <ul className='grid grid-cols-4 gap-x-2 sm:grid-cols-7'>
            {_.map(bed_length, ({
        id: id_21,
        name: name_22,
        disabled: disabled_18
      }) => <li key={id_21}>
                <Radio name='bed_length' id={_.toString(id_21)} label={name_22} checked={query?.bed_length?.toString() === id_21.toString()} onChange={() => radioChanged({
          name: 'bed_length',
          id: id_21
        })} disabled={disabled_18} />
              </li>)}
          </ul>}
      </Section>;
  const renderCabStyle = () => ignoreFilters?.includes('cab_style') ? null : <Section name='cabStyle' title='Pickup Cab Style' hasValue={!_.isEmpty(query?.cab_style)}>
        {!isFetching && _.isEmpty(cab_style) && <NoOption />}
        <ul className='grid grid-cols-2 gap-x-2 sm:grid-cols-4'>
          {_.map(cab_style, ({
        id: id_22,
        name: name_23,
        disabled: disabled_19
      }) => <li key={id_22}>
              <Checkbox id={`cab_style_${id_22}`} label={name_23} checked={_.split(query?.cab_style, ',').includes(id_22.toString())} onChange={val_19 => checkboxChanged({
          key: 'cab_style',
          id: id_22,
          val: val_19
        })} disabled={disabled_19} />
            </li>)}
        </ul>
      </Section>;
  const renderSize = () => ignoreFilters?.includes('size') ? null : <Section name='size' title='Size Segment' hasValue={!_.isEmpty(query?.size)}>
        {!isFetching && _.isEmpty(size) && <NoOption />}
        <ul className='grid grid-cols-2 gap-x-2 sm:grid-cols-4'>
          {_.map(size, ({
        id: id_23,
        name: name_24,
        disabled: disabled_20
      }) => <li key={id_23}>
              <Checkbox id={`size_${id_23}`} label={name_24} checked={_.split(query?.size, ',').includes(id_23.toString())} onChange={val_20 => checkboxChanged({
          key: 'size',
          id: id_23,
          val: val_20
        })} disabled={disabled_20} />
            </li>)}
        </ul>
      </Section>;
  const renderUpholstery = () => ignoreFilters?.includes('upholstery') ? null : <Section name='upholstery' title='Upholstery' hasValue={!_.isEmpty(query?.upholstery)}>
        {!isFetching && _.isEmpty(upholstery) && <NoOption />}
        <ul className='grid grid-cols-2 gap-x-2 sm:grid-cols-4'>
          {_.map(upholstery, ({
        id: id_24,
        name: name_25,
        disabled: disabled_21
      }) => <li key={id_24}>
              <Checkbox id={`upholstery_${id_24}`} label={name_25} checked={_.split(query?.upholstery, ',').includes(id_24.toString())} onChange={val_21 => checkboxChanged({
          key: 'upholstery',
          id: id_24,
          val: val_21
        })} disabled={disabled_21} />
            </li>)}
        </ul>
      </Section>;

  // ---- features ----
  function featureChanged(checked: boolean, id_25: number) {
    const x_1: string[] = query?.feature ? _.split(query?.feature, ',') : [];
    const y_1 = checked ? _.concat(x_1, id_25.toString()) : _.without(x_1, id_25.toString());
    setQuery({
      ...query,
      feature: _.join(y_1, ',')
    });
  }
  const renderFeatures = () => {
    return <Section name='feature' title='Features' hasValue={!_.isEmpty(query?.feature)} className='rounded-b-md'>
        <div className='space-y-3'>
          {_.map(_.keys(featureGroups), key_2 => {
          return <div key={`feature-group-${key_2}`}>
                <div className='text-sm font-light'>{key_2}:</div>
                <ul className='mt-1 grid grid-cols-1 gap-x-2 sm:grid-cols-2'>
                  {_.map(featureGroups[key_2], name_26 => {
                const {
                  id: id_26,
                  disabled: disabled_22
                } = _.find(feature, d_4 => d_4.name === name_26) || {};
                if (!id_26) {
                  return null;
                }
                return <li key={`feature-${key_2}-${id_26}`}>
                        <Checkbox id={`feature_${id_26}}`} label={name_26} checked={_.split(query?.feature, ',').includes(id_26.toString())} onChange={checked_0 => featureChanged(checked_0, id_26 as number)} disabled={disabled_22} />
                      </li>;
              })}
                </ul>
              </div>;
        })}
        </div>
      </Section>;
  };
  const renderCountry = () => ignoreFilters?.includes('assembled_in') ? null : <Section name='assembled_in' title='Assembled In' hasValue={!_.isEmpty(query?.assembled_in)}>
        {!isFetching && _.isEmpty(country) && <NoOption />}
        <ul className='grid grid-cols-2 gap-x-2 sm:grid-cols-3'>
          {_.map(country, ({
        id: id_27,
        name: name_27,
        disabled: disabled_23
      }) => <li key={id_27}>
              <Checkbox id={`country_${id_27}`} label={name_27} checked={_.split(query?.assembled_in, ',').includes(id_27.toString())} onChange={val_22 => checkboxChanged({
          key: 'assembled_in',
          id: id_27,
          val: val_22
        })} disabled={disabled_23} />
            </li>)}
        </ul>
      </Section>;
  const renderSaleStatus = () => ignoreFilters?.includes('sale_status') ? null : <Section name='sale_status' title='Sale Status' className='rounded-b-md' hasValue={!_.isEmpty(query?.sale_status)}>
        {!isFetching && _.isEmpty(sale_status) && <NoOption />}
        {!_.isEmpty(sale_status) && <ul className='grid grid-cols-1 gap-x-2 sm:grid-cols-3'>
            {_.map(sale_status, ({
        id: id_28,
        name: name_28,
        disabled: disabled_24
      }) => <li key={id_28}>
                <Checkbox id={`sale_status_id_${id_28}`} label={name_28} checked={_.split(_.toString(query?.sale_status), ',').includes(id_28?.toString())} onChange={val_23 => checkboxChanged({
          key: 'sale_status',
          id: id_28,
          val: val_23
        })} disabled={disabled_24} />
              </li>)}
          </ul>}
      </Section>;
  const renderLocation = () => ignoreFilters?.includes('location') ? null : <fieldset>
        <legend className='filter-label'>Location</legend>
        <div className='mt-2 flex w-fit items-start gap-4'>
          <div className='flex items-center'>
            <label htmlFor='zip_code' className='vehicle-s2-label mr-2'>
              Zip Code:
            </label>
            <div className='w-[65px]'>
              <Text inline={true} compact={true} name='zip_code' maxLength={5} className='w-[5ch]' type='tel' value={loc?.zip_code || ''} onChange={e => locChanged('zip_code', e.target.value)} onFocus={e_0 => e_0.target.select()} />
            </div>
          </div>
          <div className='flex items-center'>
            <span className='vehicle-s2-label mr-2'>Radius:</span>
            <div className='w-[140px] md:w-[130px]'>
              <ListBox inline={true} compact={true} option={radiusOption} value={loc?.radius || -1} onChange={val_24 => {
            locChanged('radius', val_24);
          }} />
            </div>
          </div>
        </div>
      </fieldset>;
  const NoOption = () => <div className='text-sm font-light text-gray-500'>No option available</div>;
  return <>
      {mounted && <Dialog transition={true} unmount={false} open={showFilter} onClose={() => setShowFilter(false)} className='relative z-30'>
          <DialogBackdrop className='dialog-backdrop' />
          <div className='fixed inset-0 z-20 w-screen overflow-y-scroll'>
            <div className='mx-auto max-w-[640px] lg:max-w-[1200px]'>
              <DialogPanel className='dialog-panel p-0!'>
                <PageLoading className='fixed top-2' show={isFetching} />
                {showFilter && <>
                    <div className='grid w-full grid-cols-1 gap-x-10 gap-y-8 p-6 md:p-8 lg:grid-cols-2'>
                      {/* 1 */}
                      <div className='flex flex-col gap-y-6'>
                        {renderLocation()}
                        {/* toggle */}
                        <div className='mt-2 grid grid-cols-1 gap-4 sm:grid-cols-2'>
                          {!ignoreFilters?.includes('with_photo') && <Toggle label='With Photo(s)' name='with_photo' checked={_.toNumber(query?.with_photo) === 1} onChange={isEnabled_5 => toggleChanged('with_photo', isEnabled_5)} />}
                          {!ignoreFilters?.includes('with_price') && <Toggle label='With Price' name='with_price' checked={_.toNumber(query?.with_price) === 1} onChange={isEnabled_6 => toggleChanged('with_price', isEnabled_6)} />}
                          <div className='pb-1'>
                            <Toggle label='Good Deal or Better' name='good_deal' checked={_.toNumber(query?.good_deal) === 1} onChange={isEnabled_7 => toggleChanged('good_deal', isEnabled_7)} />
                          </div>
                          {!ignoreFilters?.includes('price_dropped') && <div className='pt-1'>
                              <Toggle label='Recently Price Dropped' name='price_dropped' checked={_.toNumber(query?.price_dropped) === 1} onChange={isEnabled_8 => toggleChanged('price_dropped', isEnabled_8)} />
                            </div>}
                          {!ignoreFilters?.includes('safest') && <div className='pb-1'>
                              <Toggle label='5-Star Safety Ratings' name='safest' checked={_.toNumber(query?.safest) === 1} onChange={isEnabled_9 => toggleChanged('safest', isEnabled_9)} />
                            </div>}
                        </div>
                        {/* {Keyword()} */}
                        {renderCondition()}
                        {renderBodyType()}
                        {renderYear()}
                        {!ignoreFilters?.includes('mmt') && <fieldset className='my-1 w-full'>
                            <legend className='filter-label mb-3'>Make Model Trim</legend>
                            <div className={groupStyle}>
                              <MakeModeTrim defaultOpen={true} name='mmt1' title='Make Model Trim 1' category={category} bodyTypeId={bodyTypeId} makeOptions={make || {}} otherQuery={query} mmt={query?.mmt1 || ''} onChange={val_25 => mmtChanged('mmt1', val_25)} />
                              <MakeModeTrim name='mmt2' title='Make Model Trim 2' category={category} bodyTypeId={bodyTypeId} makeOptions={make || {}} otherQuery={query} mmt={query?.mmt2 || ''} onChange={val_26 => mmtChanged('mmt2', val_26)} />
                              <MakeModeTrim name='mmt3' title='Make Model Trim 3' category={category} bodyTypeId={bodyTypeId} makeOptions={make || {}} otherQuery={query} mmt={query?.mmt3 || ''} onChange={val_27 => mmtChanged('mmt3', val_27)} className='rounded-b-md' />
                            </div>
                          </fieldset>}
                        {renderPrice()}
                        {renderMileage()}
                        {renderHighlyRated()}
                      </div>
                      {/* 2 */}
                      <div className='flex flex-col gap-y-6'>
                        {/* Powertrain */}
                        <fieldset className='w-full'>
                          <legend className='filter-section-title pb-1'>Powertrain</legend>
                          <div className={groupStyle}>
                            {renderDriveType()}
                            {renderFuelEconomy()}
                            {renderFuelType()}
                            {renderEngineAspiration()}
                            {renderEngineCylinder()}
                            {renderEngineDisplacement()}
                            {renderTransType()}
                            {renderTransGears()}
                            {renderTowingCapacity()}
                            {renderAcceleration()}
                            {renderHorsepower()}
                            {renderPowerToWeight()}
                            {renderTorque()}
                            {renderElectricRange()}
                          </div>
                        </fieldset>
                        {/* Specifications */}
                        <fieldset className='w-full'>
                          <legend className='filter-section-title pb-1'>Specifications</legend>
                          <div className={groupStyle}>
                            {renderExteriorColor()}
                            {renderInteriorColor()}
                            {renderUpholstery()}
                            {renderSize()}
                            {renderSeats()}
                            {renderDoors()}
                            {renderCabStyle()}
                            {renderBedLength()}
                          </div>
                        </fieldset>
                        {/* other */}
                        {!ignoreFilters?.includes('other') && <fieldset className='w-full'>
                            <legend className='filter-section-title pb-1'>Other</legend>
                            <div className={groupStyle}>
                              {renderFeatures()}
                              {renderCountry()}
                              {renderSaleStatus()}
                            </div>
                          </fieldset>}
                      </div>
                    </div>
                    {/* search button  */}
                    <div className='sticky bottom-0 z-20 rounded-b-lg border-t bg-zinc-600/10 px-6 py-6 backdrop-blur dark:bg-zinc-900'>
                      <div className='flex items-center justify-between gap-x-2'>
                        <Button type='button' variant='transparent' onClick={() => setQuery({})} aria-label='Reset filters'>
                          <HiArrowUturnLeft className='mr-1 size-5' />
                          Reset
                        </Button>
                        <Button type='button' variant='primary' className='rounded-full px-6 md:px-10' onClick={applyFilter} aria-label='Search'>
                          <CruzIcon.Search className='mr-1 size-5' />
                          Search
                        </Button>
                        <Button type='button' variant='transparent' onClick={() => setShowFilter(false)} aria-label='Cancel'>
                          <CruzIcon.Close className='mr-1 size-5' />
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </>}
              </DialogPanel>
            </div>
          </div>
        </Dialog>}
      {btnType === 'home' ? <Button type='button' variant='outline' className='rounded-full pl-5 pr-6 h-12' onClick={() => setShowFilter(true)} aria-label='Show Advanced Search Filters'>
          <CruzIcon.Open className='size-5' />
          Advanced Search
        </Button> : <Button type='button' variant='secondary' className='mt-4 mb-2 h-11 rounded-full pr-6 pl-5 sm:mt-0 sm:mb-4' onClick={() => setShowFilter(true)} aria-label='Show Search Filters'>
          <CruzIcon.Search className='mr-1 size-5' />
          Search Filters
        </Button>}
      <button type='button' className={twMerge('btn-float group flex h-[50px] w-[50px] justify-center transition-all duration-300 ease-in-out hover:w-[158px]', 'bottom-[190px] hidden bg-red-800 text-white', 'ring-1 ring-white/50 2xl:ring-0 dark:ring-0', scrollTopOver && 'lg:flex')} onClick={() => setShowFilter(true)} aria-label='Show Search Filters'>
        <div className='flex w-fit items-center gap-x-2 truncate'>
          <CruzIcon.Search className='size-6' />
          <span className='hidden font-medium group-hover:inline-block'>Search Filters</span>
        </div>
      </button>
      {!_.includes(pathname, '/vehicles/dealer') && <div className={twMerge('fixed top-0 right-0 left-0 z-20 hidden bg-zinc-100/90 p-4 text-center shadow-lg backdrop-blur-xs dark:bg-zinc-800', scrollTopOver && 'block lg:hidden')}>
          <Button type='button' variant='secondary' className='h-12 w-[250px] rounded-full pr-5 pl-4' onClick={() => setShowFilter(true)} aria-label='Show Advanced Search Filters'>
            <CruzIcon.Search className='mr-1 size-5' />
            Search Filters
          </Button>
        </div>}
    </>;
}